import { useInfiniteQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import type { RecipeRankingRow } from '@fewerbags/schema';
import { Alert } from '../../components/Alert';
import { AppShell } from '../../components/AppShell';
import { EmptyState } from '../../components/EmptyState';
import { TrophyIcon } from '../../components/icons';
import { SelectField } from '../../components/SelectField';
import { CuisineCourseBadges } from '../../components/recipes/CuisineCourseBadges';
import { api } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { recipeKeys } from './queries';
import {
  COURSE_OPTIONS,
  CUISINE_OPTIONS,
  formatAvg,
  parseCourseParam,
  parseCuisineParam,
  rankNumbers,
} from './taxonomy';

const PAGE_SIZE = 50;

/** "4 ★" / "—", with a spoken label. */
function StarCell({ stars, who }: { stars: number | null; who: string }) {
  if (stars === null) {
    return (
      <span className="text-muted" aria-label={`${who}: not rated`}>
        —
      </span>
    );
  }
  return (
    <span
      className="font-semibold text-orange-ink"
      aria-label={`${who}: ${stars} ${stars === 1 ? 'star' : 'stars'}`}
    >
      {stars}
      <span aria-hidden> ★</span>
    </span>
  );
}

function RankBadge({ rank }: { rank: number | null }) {
  return (
    <span
      className={`flex size-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
        rank === null
          ? 'bg-brand-soft text-brand'
          : rank === 1
            ? 'bg-orange text-brand-dark shadow-sm ring-2 ring-orange-soft'
            : rank === 2
              ? 'bg-accent text-ink shadow-sm ring-2 ring-accent-soft'
              : rank === 3
                ? 'bg-orange-soft text-orange-ink ring-2 ring-orange/40'
                : 'bg-brand text-white'
      }`}
      aria-label={rank === null ? 'Not ranked' : `Rank ${rank}`}
    >
      {rank ?? '–'}
    </span>
  );
}

/**
 * /recipes/rankings (D26/D30): the household's recipes, plus shared ones rated
 * for someone in the household, ranked by the household's own average or one
 * person's stars. People without an account (young children) get a column
 * too. A table with a column per person on wide screens, cards on phones.
 */
export function RecipeRankingsPage() {
  const [params, setParams] = useSearchParams();
  const cuisine = parseCuisineParam(params.get('cuisine'));
  const course = parseCourseParam(params.get('course'));
  const sortBy = params.get('sortBy') || 'household';

  function setParam(key: 'cuisine' | 'course' | 'sortBy', value: string) {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === '' || (key === 'sortBy' && value === 'household')) next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );
  }

  const rankings = useInfiniteQuery({
    queryKey: recipeKeys.rankings({ cuisine, course, sortBy }),
    queryFn: ({ pageParam }) =>
      api.recipes.rankings({
        ...(cuisine === '' ? {} : { cuisine }),
        ...(course === '' ? {} : { course }),
        sortBy,
        limit: PAGE_SIZE,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((n, p) => n + p.recipes.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    placeholderData: (previous) => previous,
  });

  const people = rankings.data?.pages[0]?.people ?? [];
  const rows: RecipeRankingRow[] = rankings.data?.pages.flatMap((p) => p.recipes) ?? [];
  const total = rankings.data?.pages[0]?.total ?? 0;
  const ranks = rankNumbers(rows, sortBy);
  const nameOf = (personId: string) =>
    people.find((p) => p.personId === personId)?.name ?? 'Someone';
  const sortOptions = [
    { value: 'household', label: 'Household average' },
    ...people.map((p) => ({ value: p.personId, label: p.isMe ? `${p.name} (you)` : p.name })),
  ];
  const sortedCol = (key: string) => (key === sortBy ? 'bg-brand-soft' : '');

  return (
    <AppShell title="Rankings" width="wide">
      <div className="flex flex-col gap-4">
        <Link
          to="/recipes"
          className="inline-flex min-h-9 items-center self-start text-sm font-medium text-brand underline-offset-2 hover:underline"
        >
          ← Back to recipes
        </Link>
        <p className="text-sm text-muted">
          Your household&apos;s favourites, from your own stars only. Shared recipes appear once
          someone here has rated them.
        </p>

        <div className="card grid grid-cols-1 gap-3 p-4 sm:grid-cols-3">
          <SelectField
            id="rank-sort"
            label="Rank by"
            value={sortBy}
            options={sortOptions}
            onChange={(e) => setParam('sortBy', e.target.value)}
          />
          <SelectField
            id="rank-cuisine"
            label="Cuisine"
            value={cuisine}
            options={CUISINE_OPTIONS}
            onChange={(e) => setParam('cuisine', e.target.value)}
          />
          <SelectField
            id="rank-course"
            label="Course"
            value={course}
            options={COURSE_OPTIONS}
            onChange={(e) => setParam('course', e.target.value)}
          />
        </div>

        {rankings.isError ? (
          <Alert tone="error">
            {messageForError(rankings.error)}{' '}
            {sortBy !== 'household' ? (
              <button
                type="button"
                className="underline"
                onClick={() => setParam('sortBy', 'household')}
              >
                Rank by household average
              </button>
            ) : null}
          </Alert>
        ) : null}

        <p className="text-sm text-muted" aria-live="polite">
          {rankings.isPending
            ? 'Loading rankings…'
            : `${total} ${total === 1 ? 'recipe' : 'recipes'}`}
        </p>

        {!rankings.isPending && !rankings.isError && rows.length === 0 ? (
          <div className="card">
            <EmptyState
              accent="orange"
              icon={<TrophyIcon className="size-full text-orange-ink" />}
              title="Nothing to rank yet"
            >
              Rate a few recipes on their pages and they&apos;ll show up here.
            </EmptyState>
          </div>
        ) : null}

        {rows.length > 0 ? (
          <>
            {/* Phones: one card per recipe. */}
            <ol className="flex flex-col gap-3 md:hidden" aria-label="Ranked recipes">
              {rows.map((row, i) => (
                <li key={row.recipe.id} className="card p-3">
                  <div className="flex items-start gap-3">
                    <RankBadge rank={ranks[i] ?? null} />
                    <div className="min-w-0 flex-1">
                      <Link
                        to={`/recipes/${row.recipe.id}`}
                        className="text-base font-semibold text-brand underline-offset-2 hover:underline"
                      >
                        {row.recipe.title}
                      </Link>
                      <div className="mt-1">
                        <CuisineCourseBadges
                          cuisines={row.recipe.cuisines}
                          courses={row.recipe.courses}
                          size="xs"
                        />
                      </div>
                      <p className="mt-2 text-sm text-ink">
                        Household{' '}
                        <span className="font-semibold text-orange-ink">
                          {formatAvg(row.householdAvg)}
                        </span>
                        {row.ratedCount > 0 ? (
                          <span className="text-muted">
                            {' '}
                            ({row.ratedCount} of {people.length})
                          </span>
                        ) : null}
                      </p>
                      <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                        {people.map((m) => (
                          <li
                            key={m.personId}
                            className={
                              m.personId === sortBy ? 'rounded bg-brand-soft px-1' : undefined
                            }
                          >
                            <span className="text-muted" aria-hidden>
                              {m.name}{' '}
                            </span>
                            <StarCell stars={row.stars[m.personId] ?? null} who={m.name} />
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </li>
              ))}
            </ol>

            {/* Wider screens: a table with a star column per person. */}
            <div className="hidden overflow-x-auto card md:block">
              <table className="w-full text-left text-sm">
                <caption className="sr-only">
                  Recipes ranked by{' '}
                  {sortBy === 'household' ? 'household average' : `${nameOf(sortBy)}'s stars`}
                </caption>
                <thead className="border-b border-line text-xs text-muted uppercase">
                  <tr>
                    <th scope="col" className="px-3 py-2">
                      #
                    </th>
                    <th scope="col" className="px-3 py-2">
                      Recipe
                    </th>
                    <th scope="col" className={`px-3 py-2 text-center ${sortedCol('household')}`}>
                      Household
                    </th>
                    {people.map((m) => (
                      <th
                        key={m.personId}
                        scope="col"
                        className={`px-3 py-2 text-center ${sortedCol(m.personId)}`}
                      >
                        {m.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {rows.map((row, i) => (
                    <tr key={row.recipe.id}>
                      <td className="px-3 py-2">
                        <RankBadge rank={ranks[i] ?? null} />
                      </td>
                      <th scope="row" className="px-3 py-2 font-normal">
                        <Link
                          to={`/recipes/${row.recipe.id}`}
                          className="font-semibold text-brand underline-offset-2 hover:underline"
                        >
                          {row.recipe.title}
                        </Link>
                        <div className="mt-1">
                          <CuisineCourseBadges
                            cuisines={row.recipe.cuisines}
                            courses={row.recipe.courses}
                            size="xs"
                          />
                        </div>
                      </th>
                      <td className={`px-3 py-2 text-center ${sortedCol('household')}`}>
                        <span className="font-semibold text-orange-ink">
                          {formatAvg(row.householdAvg)}
                        </span>
                        {row.ratedCount > 0 ? (
                          <span className="block text-xs text-muted">
                            {row.ratedCount} of {people.length}
                          </span>
                        ) : null}
                      </td>
                      {people.map((m) => (
                        <td
                          key={m.personId}
                          className={`px-3 py-2 text-center ${sortedCol(m.personId)}`}
                        >
                          <StarCell stars={row.stars[m.personId] ?? null} who={m.name} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}

        {rankings.hasNextPage ? (
          <button
            type="button"
            onClick={() => void rankings.fetchNextPage()}
            disabled={rankings.isFetchingNextPage}
            className="min-h-11 self-center rounded-lg border border-line px-4 text-sm font-semibold text-brand disabled:opacity-50"
          >
            {rankings.isFetchingNextPage ? 'Loading…' : 'Load more'}
          </button>
        ) : null}
      </div>
    </AppShell>
  );
}
